import { ComponentProps, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SurveyCardSkeleton } from './SurveyCardSkeleton';
import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';

import { DeleteIcon, EditIcon, MoreVerticalIcon } from '@/assets/icons';
import { Button, Card, CardContent, IconButton, Tag, TagList } from '@/components/atoms';
import { DropdownMenuSeparator, DropdownMenuSimpleItem } from '@/components/organisms';
import { Survey_CARD_TEST_IDS } from '@/constants/data-testids';

export type SurveyCardProps = ComponentProps<'div'> & {
  img: {
    src: string;
    alt: string;
  };
  tags: string[];
  questionCount: number;
  title: string;
  description: string;
  isLoading?: boolean;
  onTemplateClick: () => void;
  onPreviewClick: () => void;
  onEditClick: () => void;
  onDeleteClick: () => void;
};

export const SurveyCard: FC<SurveyCardProps> = ({
  img,
  title,
  description,
  tags,
  questionCount,
  onDeleteClick,
  onEditClick,
  onPreviewClick,
  onTemplateClick,
  isLoading,
  ...props
}) => {
  const { t } = useTranslation('components');

  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  if (isLoading) {
    return <SurveyCardSkeleton />;
  }

  return (
    <Card
      {...props}
      data-testid={Survey_CARD_TEST_IDS.WRAPPER}
      className="rounded-[12px] p-0 group overflow-hidden"
      onMouseLeave={() => setIsOpenDropdown(false)}
    >
      <CardContent>
        <div className="relative">
          <img
            src={img.src}
            alt={img.alt}
            className="object-cover object-top w-full aspect-[258/100]"
          />
          <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-200 pointer-events-none" />

          <Tag
            label={`${questionCount} questions`}
            size="regular"
            color="grey"
            type="outlined"
            className="absolute z-10 top-2.5 left-2.5 bg-custom-warm-grey-50 outline-custom-warm-grey-200"
          />

          <div className="absolute z-10 top-2.5 right-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <Popover open={isOpenDropdown} onOpenChange={setIsOpenDropdown}>
              <PopoverTrigger asChild>
                <IconButton
                  size="sm"
                  variant="secondary"
                  icon={<MoreVerticalIcon width={20} height={20} />}
                />
              </PopoverTrigger>
              <PopoverContent className="min-w-[220px]" side="bottom" align="end" sideOffset={8}>
                <div className="bg-custom-warm-grey-25 rounded-[12px] border border-custom-warm-grey-200 p-2">
                  <DropdownMenuSimpleItem onClick={onEditClick}>
                    <span className="text-custom-warm-grey-500">
                      <EditIcon width={20} height={20} />
                    </span>
                    <p className='text-body-14-regular text-custom-warm-grey-800"'>
                      {t('surveyCard.dropdown.edit')}
                    </p>
                  </DropdownMenuSimpleItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuSimpleItem onClick={onDeleteClick}>
                    <span className="text-custom-warm-grey-500">
                      <DeleteIcon width={20} height={20} />
                    </span>
                    <p className="text-body-14-regular text-custom-warm-grey-800">
                      {t('surveyCard.dropdown.delete')}
                    </p>
                  </DropdownMenuSimpleItem>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
        <div className="px-[16px] py-[14px]">
          <div className="mb-3">
            <TagList tags={tags} />
          </div>
          <div className="mb-6">
            <p className="text-body-16-medium text-custom-warm-grey-800 mb-2">{title}</p>
            <p className="text-body-14-regular text-custom-warm-grey-500">{description}</p>
          </div>
          <div className="flex items-center gap-2 justify-between">
            <Button
              onClick={onTemplateClick}
              variant="secondary"
              size="sm"
              className="max-w-1/2 w-full"
            >
              {t('surveyCard.useTemplate')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 max-w-1/2 w-full"
              onClick={onPreviewClick}
            >
              {t('surveyCard.preview')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
